import { useState, useEffect, lazy, Suspense } from "react";
import "@/App.css";
import "@/styles/rockfall-theme.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";
import Layout from "@/components/Layout";
import ProtectedRoute from "@/components/ProtectedRoute";
import ThemeToggle from "@/components/ThemeToggle";

// Lazy load all pages for code splitting (reduces initial bundle size)
const AuthPage = lazy(() => import("@/pages/AuthPage"));
const ForgotPassword = lazy(() => import("@/pages/ForgotPassword"));
const ResetPassword = lazy(() => import("@/pages/ResetPassword"));
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const AdminDashboard = lazy(() => import("@/pages/AdminDashboard"));
const HomeBoard = lazy(() => import("@/pages/HomeBoard"));
const SharpeningMenu = lazy(() => import("@/pages/SharpeningMenu"));
const NewTrackingFull = lazy(() => import("@/pages/NewTrackingFull"));
const AddDrillBitPage = lazy(() => import("@/pages/AddDrillBitPage"));
const HistoryPage = lazy(() => import("@/pages/HistoryPage"));
const DeliveryPage = lazy(() => import("@/pages/DeliveryPage"));
const PickupPage = lazy(() => import("@/pages/PickupPage"));
const DeliverPage = lazy(() => import("@/pages/DeliverPage"));
const DeliveryConfirmPage = lazy(() => import("@/pages/DeliveryConfirmPage"));
const DeliveryHistoryPage = lazy(() => import("@/pages/DeliveryHistoryPage"));
const AdministrationPage = lazy(() => import("@/pages/AdministrationPage"));
const SignupPage = lazy(() => import("@/pages/SignupPage"));
const StatisticsDetailPage = lazy(() => import("@/pages/StatisticsDetailPage"));
const ProfilePage = lazy(() => import("@/pages/ProfilePage"));
const Contacts = lazy(() => import("@/pages/Contacts"));
const ContactDetail = lazy(() => import("@/pages/ContactDetail"));
const SharpeningTrackingPage = lazy(() => import("@/pages/SharpeningTrackingPage"));
const CompleteSharpeningPage = lazy(() => import("@/pages/CompleteSharpeningPage"));
const CatalogPage = lazy(() => import("@/pages/CatalogPage"));
const PerformancePage = lazy(() => import("@/pages/PerformancePage"));

// Loading fallback component
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-purple-50 to-indigo-100">
    <div className="text-center">
      <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-indigo-600 mx-auto mb-4"></div>
      <p className="text-gray-600 font-medium">Chargement...</p>
    </div>
  </div>
);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Configuration globale axios pour réseau mobile
axios.defaults.timeout = 60000; // 60s au lieu de 10s par défaut
axios.defaults.headers.common['Accept-Encoding'] = 'gzip';

// Désactiver console.log en production pour meilleures performances
if (process.env.NODE_ENV === 'production') {
  console.log = () => {};
  console.debug = () => {};
  console.info = () => {};
}

// Axios interceptor for auth
axios.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      window.location.href = "/login";
    }
    return Promise.reject(error);
  }
);

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) {
      setIsAuthenticated(true);
    }
    setLoading(false);
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="App">
      <BrowserRouter>
        <Suspense fallback={<PageLoader />}>
          <Routes>
          <Route
            path="/login"
            element={
              !isAuthenticated ? (
                <AuthPage setIsAuthenticated={setIsAuthenticated} />
              ) : (
                <Navigate to="/dashboard" />
              )
            }
          />
          <Route path="/signup" element={<SignupPage />} />
          <Route
            path="/forgot-password"
            element={<ForgotPassword />}
          />
          <Route
            path="/reset-password"
            element={<ResetPassword />}
          />
          <Route
            path="/"
            element={
              isAuthenticated ? (
                <Navigate to="/dashboard" />
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/dashboard"
            element={
              isAuthenticated ? (
                <HomeBoard setIsAuthenticated={setIsAuthenticated} />
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/crm"
            element={
              isAuthenticated ? (
                <Layout setIsAuthenticated={setIsAuthenticated}>
                  <Dashboard />
                </Layout>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/home"
            element={
              isAuthenticated ? (
                <HomeBoard setIsAuthenticated={setIsAuthenticated} />
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/sharpening-menu"
            element={
              isAuthenticated ? (
                <ProtectedRoute requiredModules={["suivi_aiguisage", "ajout_trepan"]}>
                  <SharpeningMenu setIsAuthenticated={setIsAuthenticated} />
                </ProtectedRoute>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/new-tracking"
            element={
              isAuthenticated ? (
                <ProtectedRoute requiredModules={["suivi_aiguisage"]}>
                  <NewTrackingFull />
                </ProtectedRoute>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/add-drill-bit"
            element={
              isAuthenticated ? (
                <ProtectedRoute requiredModules={["ajout_trepan"]}>
                  <AddDrillBitPage />
                </ProtectedRoute>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/sharpening-history"
            element={
              isAuthenticated ? (
                <ProtectedRoute requiredModules={["suivi_aiguisage"]}>
                  <HistoryPage />
                </ProtectedRoute>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/delivery"
            element={
              isAuthenticated ? (
                <ProtectedRoute requiredModules={["ramassage", "livraison"]}>
                  <DeliveryPage setIsAuthenticated={setIsAuthenticated} />
                </ProtectedRoute>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/delivery/pickup"
            element={
              isAuthenticated ? (
                <ProtectedRoute requiredModules={["ramassage"]}>
                  <PickupPage />
                </ProtectedRoute>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/delivery/dropoff"
            element={
              isAuthenticated ? (
                <ProtectedRoute requiredModules={["livraison"]}>
                  <DeliverPage />
                </ProtectedRoute>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/delivery/confirm"
            element={
              isAuthenticated ? (
                <ProtectedRoute requiredModules={["livraison"]}>
                  <DeliveryConfirmPage />
                </ProtectedRoute>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/delivery/history"
            element={
              isAuthenticated ? (
                <DeliveryHistoryPage setIsAuthenticated={setIsAuthenticated} />
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/sharpening-tracking"
            element={
              isAuthenticated ? (
                <ProtectedRoute requiredModules={["suivi_aiguisage"]}>
                  <SharpeningTrackingPage />
                </ProtectedRoute>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/sharpening-tracking/complete/:pickupId"
            element={
              isAuthenticated ? (
                <ProtectedRoute requiredModules={["suivi_aiguisage"]}>
                  <CompleteSharpeningPage />
                </ProtectedRoute>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/administration"
            element={
              isAuthenticated ? (
                <ProtectedRoute requiredRoles={["admin", "admin_entrepot", "vendeur"]}>
                  <AdministrationPage setIsAuthenticated={setIsAuthenticated} />
                </ProtectedRoute>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/statistics/:type"
            element={
              isAuthenticated ? (
                <StatisticsDetailPage />
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/profile"
            element={
              isAuthenticated ? (
                <ProfilePage setIsAuthenticated={setIsAuthenticated} />
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/admin-dashboard"
            element={
              isAuthenticated ? (
                <ProtectedRoute requiredRoles={["admin", "admin_entrepot"]}>
                  <AdminDashboard />
                </ProtectedRoute>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/catalog"
            element={
              isAuthenticated ? (
                <CatalogPage setIsAuthenticated={setIsAuthenticated} />
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/performance"
            element={
              isAuthenticated ? (
                <PerformancePage />
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/contacts"
            element={
              isAuthenticated ? (
                <Layout setIsAuthenticated={setIsAuthenticated}>
                  <Contacts />
                </Layout>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/contacts/:id"
            element={
              isAuthenticated ? (
                <Layout setIsAuthenticated={setIsAuthenticated}>
                  <ContactDetail />
                </Layout>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
        </Routes>
        </Suspense>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
      <ThemeToggle />
    </div>
  );
}

export default App;
